import { useState, useCallback, useRef } from "react";
import { getAuth } from "firebase/auth";
import { isNetworkError, NETWORK_ERROR_MESSAGE } from "../utils/apiError";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const SLOW_API_BASE_URL = import.meta.env.VITE_SLOW_API_BASE_URL;

export interface FailedRequest {
  requestId: string;
  baseUrl: string;
  method: string;
  path: string;
  elapsedMs: number;
}

// type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

// `signal` is deliberately not accepted: only get() is abortable, and it owns its own
// controller. Mutations must never be abortable — an aborted POST/PATCH still commits
// server-side, so the caller would report a failure for a write that actually happened.
interface RequestOptions extends Omit<RequestInit, "headers" | "signal"> {
  headers?: Record<string, string>;
  noAuth?: boolean;
  slowApi?: boolean;
  includeCredentials?: boolean;
  _isRetry?: boolean;
}

type InternalRequestOptions = RequestOptions & { signal?: AbortSignal; _text?: boolean; _blob?: boolean };

interface UseApiReturn {
  loading: boolean;
  error: string | null;
  /**
   * Supersedes any in-flight GET to the same path: the superseded promise never settles,
   * so none of `.then` / `.catch` / `.finally` run for it. Drive spinners off `loading`
   * above, never off a local flag cleared in a `finally` — that flag would never clear.
   */
  get<T>(url: string, options?: RequestOptions): Promise<T>;
  post<T>(url: string, body: any, options?: RequestOptions): Promise<T>;
  put<T>(url: string, body: any, options?: RequestOptions): Promise<T>;
  patch<T>(url: string, body: any, options?: RequestOptions): Promise<T>;
  del<T>(url: string, options?: RequestOptions): Promise<T>;
  postForm<T>(url: string, body: FormData, options?: RequestOptions): Promise<T>;
  download(url: string, options?: RequestOptions): Promise<void>;
  /**
   * A text/html response, for the evidence preview. An iframe `src` sends no Authorization
   * header, so the HTML is fetched here and handed to the iframe's `srcDoc` instead.
   */
  getText(url: string, options?: RequestOptions): Promise<string>;
  /**
   * An image response, for a thumbnail. An `<img src>` sends no Authorization header, so the
   * bytes are fetched here and handed to the tag as an object URL instead.
   */
  getBlob(url: string, options?: RequestOptions): Promise<Blob>;
}

async function throwResponseError(response: Response): Promise<never> {
  let errorMessage = `HTTP error! status: ${response.status}`;
  let errorData = null;

  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } else {
      const textResponse = (await response.text()).trim();
      // A gateway's HTML page is not a message, so the status line stays the message instead.
      if (textResponse && !textResponse.startsWith('<')) {
        errorMessage = textResponse;
      }
    }
  } catch (parseError) {
    console.warn('Failed to parse error response:', parseError);
  }

  const error = new Error(errorMessage) as Error & {
    status: number;
    data: any;
    response: Response;
  };
  error.status = response.status;
  error.data = errorData;
  error.response = response;

  throw error;
}

// The 401 `code`s that end a session, mapped to their /signin `msg`.
const SESSION_END_MESSAGES = {
  SESSION_EXPIRED: 'session_expired',
  ACCOUNT_DISABLED: 'account_disabled',
} as const;

type SessionEndCode = keyof typeof SESSION_END_MESSAGES;

function isSessionEndCode(code: unknown): code is SessionEndCode {
  return typeof code === 'string' && code in SESSION_END_MESSAGES;
}

async function sessionEndCode(response: Response): Promise<SessionEndCode | null> {
  if (response.status !== 401) return null;
  const code = (await response.clone().json().catch(() => null))?.code;
  return isSessionEndCode(code) ? code : null;
}

async function endSession(code: SessionEndCode): Promise<void> {
  await getAuth().signOut();
  window.location.assign(`/signin?msg=${SESSION_END_MESSAGES[code]}`);
}

export function useApi(): UseApiReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightGets = useRef(new Map<string, AbortController>());
  const inFlightCount = useRef(0);

  const request = useCallback(async <T>(url: string, options: InternalRequestOptions = {}): Promise<T> => {
    // Counted, not a boolean: pages run several requests through one useApi() instance,
    // and the first to finish must not clear the spinner for the ones still in flight.
    inFlightCount.current += 1;
    setLoading(true);
    setError(null);

    const requestId = crypto.randomUUID();
    const baseUrl = options.slowApi ? SLOW_API_BASE_URL : API_BASE_URL;
    const startedAt = performance.now();

    try {
      // FormData bodies set their own multipart boundary; forcing JSON breaks them.
      const headers: Record<string, string> = {
        ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
      };
      headers["X-Request-Id"] = requestId;

      // Only add authentication if noAuth is not set
      if (!options.noAuth) {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error("No user logged in");

        const token = await user.getIdToken();
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${baseUrl}${url}`, {
        ...options,
        headers,
        ...(options.includeCredentials && { credentials: 'include' }),
      } as RequestInit); // ✅ Explicit cast ensures compatibility

      if (!response.ok) {
        const endCode = await sessionEndCode(response);
        if (endCode) {
          await endSession(endCode);
          // endSession() hard-navigates to /signin (window.location.assign
          // unloads the document). Never settle: callers' `await` stays pending
          // until the page is torn down, so they neither crash on a fabricated
          // value nor report the expired session to Sentry. Relies on
          // endSession() doing a full page load, not SPA navigation.
          return new Promise<T>(() => {});
        }

        if (response.status === 401 && !options._isRetry) {
          const code = (await response.clone().json().catch(() => null))?.code;
          if (code === 'ACCESS_DENIED') {
            // Company membership may have been revoked — refresh token and retry once
            const user = getAuth().currentUser;
            if (user) {
              await user.getIdToken(true);
              return request<T>(url, { ...options, _isRetry: true });
            }
          }
        }

        await throwResponseError(response);
      }

      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as T;
      }

      if (options._text) {
        return (await response.text()) as T;
      }

      if (options._blob) {
        return (await response.blob()) as T;
      }

      return (await response.json()) as T;
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // Superseded by a newer GET to the same endpoint. Never settle, for the same
        // reason as the session-end path above: the caller's `await` is abandoned, so a
        // stale response can't overwrite fresher data and callers need no AbortError
        // branch in their catch blocks.
        return new Promise<T>(() => {});
      }
      if (err instanceof TypeError) {
        // fetch only throws a TypeError when the request never reached the API. Marked rather than
        // reworded, so a caller can say what did not happen in its own words.
        const failed = err as TypeError & { failedRequest?: FailedRequest; network?: true };
        failed.network = true;
        failed.failedRequest = { requestId, baseUrl, method: options.method ?? "GET", path: url, elapsedMs: Math.round(performance.now() - startedAt) };
      }
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : (err.message ?? "Unknown error"));
      throw err;
    } finally {
      inFlightCount.current -= 1;
      if (inFlightCount.current === 0) setLoading(false);
    }
  }, []); // useState setters and refs are all stable, so there is nothing to depend on

  // Keyed by path so a refetch supersedes only the previous request for the same
  // resource — pages drive several endpoints off one useApi() instance in parallel.
  const abortPrevious = useCallback((url: string) => {
    const endpoint = url.split('?')[0];
    inFlightGets.current.get(endpoint)?.abort();
    const controller = new AbortController();
    inFlightGets.current.set(endpoint, controller);
    return controller;
  }, []);

  const get = useCallback(<T>(url: string, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    const controller = abortPrevious(url);
    return request<T>(url, { method: "GET", ...requestOptions, noAuth, slowApi, includeCredentials, signal: controller.signal });
  }, [request, abortPrevious]);

  const getText = useCallback((url: string, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    // Same supersede-by-path rule as get(): two preview fetches for one draft must not race.
    const controller = abortPrevious(url);
    return request<string>(url, { method: "GET", ...requestOptions, noAuth, slowApi, includeCredentials, _text: true, signal: controller.signal });
  }, [request, abortPrevious]);

  const getBlob = useCallback((url: string, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    const controller = abortPrevious(url);
    return request<Blob>(url, { method: "GET", ...requestOptions, noAuth, slowApi, includeCredentials, _blob: true, signal: controller.signal });
  }, [request, abortPrevious]);

  const post = useCallback(<T>(url: string, body: any, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    return request<T>(url, { method: "POST", body: JSON.stringify(body), ...requestOptions, noAuth, slowApi, includeCredentials });
  }, [request]);

  const postForm = useCallback(<T,>(url: string, body: FormData, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    return request<T>(url, { method: "POST", body, ...requestOptions, noAuth, slowApi, includeCredentials });
  }, [request]);

  const put = useCallback(<T>(url: string, body: any, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    return request<T>(url, { method: "PUT", body: JSON.stringify(body), ...requestOptions, noAuth, slowApi, includeCredentials });
  }, [request]);

  const patch = useCallback(<T>(url: string, body: any, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    return request<T>(url, { method: "PATCH", body: JSON.stringify(body), ...requestOptions, noAuth, slowApi, includeCredentials });
  }, [request]);

  const del = useCallback(<T>(url: string, options?: RequestOptions) => {
    const { noAuth, slowApi, includeCredentials, ...requestOptions } = options || {};
    return request<T>(url, { method: "DELETE", ...requestOptions, noAuth, slowApi, includeCredentials });
  }, [request]);

  const download = useCallback(async (url: string, options?: RequestOptions): Promise<void> => {
    inFlightCount.current += 1;
    setLoading(true);
    setError(null);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("No user logged in");
      const token = await user.getIdToken();

      const response = await fetch(`${options?.slowApi ? SLOW_API_BASE_URL : API_BASE_URL}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const endCode = await sessionEndCode(response);
        if (endCode) {
          await endSession(endCode);
          // Never settle — page is hard-navigating to /signin (see request()).
          return new Promise<void>(() => {});
        }
        await throwResponseError(response);
      }

      const blob = await response.blob();

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      if (!contentDisposition) {
        console.error('Cannot find Content-Disposition header');
        throw new Error('Invalid response headers');
      }

      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (!filenameMatch) {
        console.error('Cannot find filename in Content-Disposition header');
        throw new Error('Invalid response headers');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filenameMatch[1];
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err: any) {
      // A download runs its own fetch, so it marks a dropped connection the same way request() does.
      if (err instanceof TypeError) (err as TypeError & { network?: true }).network = true;
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : (err.message ?? "Unknown error"));
      throw err;
    } finally {
      inFlightCount.current -= 1;
      if (inFlightCount.current === 0) setLoading(false);
    }
  }, []);

  return { loading, error, get, getText, getBlob, post, postForm, put, patch, del, download };
}
